import { createContext, useContext } from "react";
import type { Identifier } from "ra-core";
import type { ClientSmsDraft, Contact, Conversation } from "@/modules/types";

export type MessagesQuickAccessContextValue = {
  openSms: (
    contact: Contact,
    dealId?: Identifier | null,
    options?: { initialBody?: string | null },
  ) => Promise<void>;
  openSmsToPhone: (phone: string) => Promise<void>;
  isOpening: boolean;
  isDockOpen: boolean;
  focusedConversation: Conversation | null;
  draftSms: ClientSmsDraft | null;
  /** One-shot composer prefill (e.g. share dialogs). */
  smsPrefillRequest: { key: number; text: string } | null;
  clearSmsPrefillRequest: () => void;
  activeConversationId: Identifier | null;
  clearFocusedConversation: () => void;
  setDraftSms: (draft: ClientSmsDraft | null) => void;
  clearDraftSms: () => void;
  focusConversation: (conversation: Conversation) => void;
  setActiveConversationId: (conversationId: Identifier | null) => void;
  markConversationRead: (conversationId: Identifier, readAt?: string) => void;
  viewConversation: (conversation: Conversation) => void;
  openInbox: () => void;
  closeInbox: () => void;
  toggleInbox: () => void;
};

export const MessagesQuickAccessContext =
  createContext<MessagesQuickAccessContextValue | null>(null);

export const useMessagesQuickAccess = () => {
  const context = useContext(MessagesQuickAccessContext);
  if (!context) {
    throw new Error(
      "useMessagesQuickAccess must be used within MessagesQuickAccessProvider",
    );
  }
  return context;
};

export const useMessagesQuickAccessOptional = () =>
  useContext(MessagesQuickAccessContext);
